import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import Container from "@cloudscape-design/components/container"
import ExpandableSection from "@cloudscape-design/components/expandable-section"
import FormField from "@cloudscape-design/components/form-field"
import Select from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { UseFormRegister, UseFormWatch, UseFormSetValue, FieldErrors } from "react-hook-form"

import { DiffBadge } from "../DiffBadge"
import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { DQDL_RULES } from "./constants"
import { DqdlRuleEditor } from "./DqdlRuleEditor"
import { FieldMultiSelect } from "./FieldMultiSelect"
import { SDSRuleBadge } from "./SDSRuleBadge"
import { DqdlRule, DataQualityFormData, RuleType, EdcSourceRule } from "./types"
import { MetadataField } from "./useMetadataFields"

/**
 * Props for the ConformanceSection component.
 */
interface ConformanceSectionProps {
  /**
   * Section title (e.g., 'Major Rules', 'Minor Rules')
   */
  title: string

  /**
   * Description text shown below the title.
   */
  description: string

  /**
   * Field array items for DQDL rules (from useFieldArray)
   */
  fields: { id: string }[]

  /**
   * Function to append a new DQDL rule.
   */
  append: (rule: DqdlRule) => void

  /**
   * Function to remove a DQDL rule by index.
   */
  remove: (index: number) => void

  /**
   * React-hook-form register function for binding inputs.
   */
  register: UseFormRegister<DataQualityFormData>

  /**
   * React-hook-form watch function for observing field values.
   */
  watch: UseFormWatch<DataQualityFormData>

  /**
   * React-hook-form setValue function for programmatic updates.
   */
  setValue: UseFormSetValue<DataQualityFormData>

  /**
   * Which rule array this section manages ('majorRules' or 'minorRules')
   */
  prefix: "majorRules" | "minorRules"

  /**
   * Which PII rule this section manages ('majorPii' or 'minorPii')
   */
  piiPrefix: "majorPii" | "minorPii"

  /**
   * Whether the form is in edit mode.
   */
  canEdit: boolean

  /**
   * Form validation errors.
   */
  errors?: FieldErrors<DataQualityFormData>

  /**
   * Available fields from metadata for dropdowns.
   */
  availableFields: MetadataField[]

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void

  /**
   * SDS rules currently being translated (shown as pending placeholder blocks).
   */
  pendingRules?: EdcSourceRule[]

  /**
   * Inline diff data for showing change badges.
   */
  inlineDiff?: Map<string, string> | null

  /**
   * DTA ID for badge links.
   */
  dtaId?: string

  /**
   * Current version for badge links.
   */
  version?: string
}

/**
 * Section component for managing DQDL conformance rules and PII detection fields. Used for both
 * Major and Minor conformance stages.
 */
export const ConformanceSection: React.FC<ConformanceSectionProps> = ({
  title,
  description,
  fields,
  append,
  remove,
  register,
  watch,
  setValue,
  prefix,
  piiPrefix,
  canEdit,
  errors,
  availableFields,
  onNavigateToMetadata,
  pendingRules = [],
  inlineDiff,
  dtaId,
  version,
}) => {
  const piiColumns = watch(`${piiPrefix}.columns`) || []
  const hasPiiRule = piiColumns.length > 0 || watch(`${piiPrefix}.enabled`)

  const enabledCount = fields.filter((_, i) => watch(`${prefix}.${i}`)?.enabled !== false).length
  const allEnabled = fields.length > 0 && enabledCount === fields.length
  const someEnabled = enabledCount > 0 && enabledCount < fields.length

  const toggleAll = () => {
    const newValue = enabledCount < fields.length
    fields.forEach((_, i) => setValue(`${prefix}.${i}.enabled`, newValue))
  }

  const addPiiRule = () => {
    setValue(`${piiPrefix}.enabled`, true)
  }

  const removePiiRule = () => {
    setValue(`${piiPrefix}.columns`, [])
    setValue(`${piiPrefix}.enabled`, false)
  }

  const handlePiiColumnsChange = (columns: string[]) => {
    setValue(`${piiPrefix}.columns`, columns, { shouldDirty: true })
  }

  // ColumnDataType is derived-only: generated from the Metadata tab's declared
  // Format and shown read-only in "Derived from Metadata", so it is not offered
  // as a manually authorable rule type.
  const ruleTypeOptions = Object.entries(DQDL_RULES)
    .filter(([key]) => key !== "ColumnDataType")
    .map(([key, { label }]) => ({
      value: key,
      label,
    }))

  return (
    <ExpandableSection
      variant="container"
      defaultExpanded
      headerText={title}
      headerDescription={description}
      headerActions={
        canEdit && fields.length > 0 ? (
          <Checkbox
            checked={allEnabled}
            indeterminate={someEnabled}
            onChange={toggleAll}
            ariaLabel={`Toggle all ${title}`}
          >
            All enabled
          </Checkbox>
        ) : undefined
      }
    >
      <SpaceBetween size="m">
        {/* Pending SDS rules (translation in progress) */}
        {pendingRules.map((rule) => (
          <Container
            key={rule.rule_name}
            header={
              <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                <Badge color="blue">Translating...</Badge>
                <Box variant="strong">{rule.rule_name}</Box>
              </SpaceBetween>
            }
          >
            <Box color="text-body-secondary">{rule.description}</Box>
          </Container>
        ))}

        {fields.map((field, i) => {
          const rule = watch(`${prefix}.${i}`)
          const key = `${String(rule?.type)}:${String(rule?.column ?? "")}`
          const diffStatus = inlineDiff?.get(key)
          return (
            <Container key={field.id}>
              <SpaceBetween size="s">
                <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                  {canEdit ? (
                    <Select
                      selectedOption={
                        ruleTypeOptions.find((o) => o.value === (rule?.type || "IsComplete")) ??
                        null
                      }
                      onChange={({ detail }) =>
                        setValue(`${prefix}.${i}.type`, detail.selectedOption.value as RuleType)
                      }
                      options={ruleTypeOptions}
                      ariaLabel="Rule type"
                    />
                  ) : (
                    <ReadOnlyValue
                      value={
                        ruleTypeOptions.find((o) => o.value === (rule?.type || "IsComplete"))
                          ?.label ?? rule?.type
                      }
                    />
                  )}
                  {rule?.source === "sds" && (
                    <SDSRuleBadge
                      confidence={rule.confidence ?? undefined}
                      sourceReference={rule.source_reference ?? undefined}
                      ruleName={rule.rule_name ?? undefined}
                      reasoning={rule.reasoning ?? undefined}
                    />
                  )}
                  {rule?.source === "inferred" && <Badge color="grey">System generated</Badge>}
                  {diffStatus && dtaId && version && (
                    <DiffBadge
                      status={diffStatus as "new" | "modified"}
                      version={version}
                      dtaId={dtaId}
                      section="dqRules"
                      item={key}
                    />
                  )}
                  {canEdit && (
                    <SpaceBetween size="s" direction="horizontal" alignItems="center">
                      <Checkbox
                        checked={rule?.enabled !== false}
                        onChange={({ detail }) =>
                          setValue(`${prefix}.${i}.enabled`, detail.checked)
                        }
                      >
                        Enabled
                      </Checkbox>
                      <Button variant="inline-link" onClick={() => remove(i)}>
                        Remove
                      </Button>
                    </SpaceBetween>
                  )}
                </SpaceBetween>
                {rule && (
                  <DqdlRuleEditor
                    rule={rule}
                    index={i}
                    prefix={prefix}
                    register={register}
                    setValue={setValue}
                    canEdit={canEdit}
                    errors={errors}
                    availableFields={availableFields}
                    onNavigateToMetadata={onNavigateToMetadata}
                  />
                )}
              </SpaceBetween>
            </Container>
          )
        })}

        {canEdit && (
          <Button
            variant="inline-link"
            iconName="add-plus"
            onClick={() => append({ type: "IsComplete" })}
          >
            Add DQDL Rule
          </Button>
        )}

        <SpaceBetween size="xs">
          <Box variant="h5" padding="n">
            PII Detection
          </Box>
          <Box variant="small" color="text-body-secondary">
            Scan columns for personally identifiable information. If PII is detected, the entire
            upload is quarantined.
          </Box>
          <Box>
            {hasPiiRule ? (
              <Container
                header={
                  <SpaceBetween size="xs" direction="horizontal" alignItems="center">
                    <Box variant="strong">Columns to scan</Box>
                    {canEdit && (
                      <Button variant="inline-link" onClick={removePiiRule}>
                        Remove
                      </Button>
                    )}
                  </SpaceBetween>
                }
              >
                <FormField description="Columns to scan for PII">
                  <FieldMultiSelect
                    value={piiColumns}
                    onChange={handlePiiColumnsChange}
                    availableFields={availableFields}
                    readOnly={!canEdit}
                    placeholder="Add column"
                    onNavigateToMetadata={onNavigateToMetadata}
                  />
                </FormField>
              </Container>
            ) : (
              canEdit && (
                <Button variant="inline-link" iconName="add-plus" onClick={addPiiRule}>
                  Add PII Rule
                </Button>
              )
            )}
          </Box>
        </SpaceBetween>
      </SpaceBetween>
    </ExpandableSection>
  )
}
